import { IOrg } from "#webapp/src/__schema__";
import { getClient } from "#webapp/src/network/apollo/client";
import {
  lastChromeWrapperMountTimestamp,
  lastFeatureGridRefetchTimestamp,
  lastTabFocusTimestamp,
} from "@clockwise/web-commons/src/util/session-storage";
import { DocumentNode } from "graphql";
import { DateTime } from "luxon";
import React from "react";
import { FeatureGridQuery } from "./eventHandlerFeatureGridRefetch.gql";

interface Props {
  onHover: (hovered: boolean, event?: React.MouseEvent<HTMLDivElement>) => void;
  orgId?: string;
  setFeatureGridStatesFromOrg: (org: IOrg) => void;
}

export const eventHandlerFeatureGridRefetch = ({
  onHover,
  orgId,
  setFeatureGridStatesFromOrg,
}: Props) => {
  let isMouseInWindow = true;

  const initListeners = () => {
    document.body.addEventListener("mouseover", onMouseOverBody);
    document.body.addEventListener("mouseleave", onMouseLeaveWindow);
    document.addEventListener("visibilitychange", onTabFocusChange);
  };

  const removeListeners = () => {
    document.body.removeEventListener("mouseover", onMouseOverBody);
    document.body.removeEventListener("mouseleave", onMouseLeaveWindow);
    document.removeEventListener("visibilitychange", onTabFocusChange);
  };

  const onMouseOverBody = (e: Event) => {
    // check when the mouse moves back into the iFrame after onMouseLeaveWindow

    if (!isMouseInWindow) {
      if (orgId) {
        maybeRefetchData(orgId);
      }

      isMouseInWindow = true;
    }
    // failsafe - if we manage to hover on the body, we are no longer on the extension
    onHover(e.target !== document.body);
  };

  const onMouseLeaveWindow = () => {
    // only set when user leave extension or webapp to prevent too many session storage calls
    isMouseInWindow = false;
    onHover(false);
  };

  const onTabFocusChange = () => {
    if (document.visibilityState === "visible" || document.visibilityState === "hidden") {
      const now = DateTime.now();
      if (document.visibilityState === "visible" && orgId) {
        maybeRefetchData(orgId);
      }

      lastTabFocusTimestamp.set(now);
    }
  };

  const maybeRefetchData = async (orgId: string) => {
    const now = DateTime.now();
    const lastMount = lastChromeWrapperMountTimestamp.get();
    const lastRefetchFeatureGrid = lastFeatureGridRefetchTimestamp.get();
    // Have not mounted or refetched since yeseterday
    const isDataStale =
      isTimestampBeforeToday(lastMount) && isTimestampBeforeToday(lastRefetchFeatureGrid);

    const isAfterFourPmAndNotRefetched =
      isAfterFourPm(now) &&
      isTimestampBeforeFourPmLocal(lastRefetchFeatureGrid) &&
      isTimestampBeforeFourPmLocal(lastMount);

    if (isDataStale || isAfterFourPmAndNotRefetched) {
      lastFeatureGridRefetchTimestamp.set(DateTime.now());
      const response = await refetch(orgId, FeatureGridQuery);
      if (response?.data) {
        setFeatureGridStatesFromOrg(response.data.node);
      }
    }
  };
  return { initListeners, removeListeners };
};

// Helpers

const refetch = async (orgId: string, query: DocumentNode) => {
  const client = getClient();
  const response = await client.query({ query: query, variables: { orgRelayId: orgId } });
  return response;
};

export const isTimestampBeforeToday = (date: DateTime | null | undefined) => {
  if (!date) return true;
  const now = DateTime.now();
  return now.startOf("day") > date.startOf("day");
};

export const isAfterFourPm = (now: DateTime) => {
  return now.hour >= 16;
};

export const isTimestampBeforeFourPmLocal = (date: DateTime | null) => {
  if (!date) return true;
  return date.hour < 16;
};

export const refetchFeatureGridData = async (orgId: string) => {
  const response = await refetch(orgId, FeatureGridQuery);
  if (response?.data) {
    return response.data.node;
  }
};
